<template>
  <section
    id="product-details"
    :class="{'details-optimization': shoppableRedesignOptimization}"
  >
    <header class="header">
      <h3 class="title">
        Product details
      </h3>
      <span class="product-id">
        Web ID: {{ productId }}
      </span>
    </header>

    <div
      class="expandable-wrapper"
      :class="[{ 'open' : isOpen }, spacingClass]"
      :style="isSwitchButtonAvailable ? {'--blur-height': CLOSE_WRAPPER_HEIGHT + 'px'} : {}"
    >
      <div
        ref="wrapper"
        class="wrapper"
        :class="additionalClasses"
      >
        <p
          v-if="!!productDescription"
          data-auto="product-long-description-section"
          class="long-description"
          v-html="productDescription"
        />

        <div class="details">
          <ul
            v-for="(column, i) in columns"
            :key="`column-${i}`"
            class="column"
          >
            <li
              v-for="item in column"
              :key="item.dataAuto"
              :data-auto="item.dataAuto"
              class="column-item"
            >
              <h4
                v-if="!!item.title"
                class="medium margin-bottom-xxs"
              >
                {{ item.title }}
              </h4>
              <component :is="item.component" />
            </li>
          </ul>
        </div>
      </div>

      <div
        v-if="isSwitchButtonAvailable"
        class="switch-wrapper"
      >
        <button
          class="switch link-med"
          @click="handleButtonClick"
        >
          {{ buttonText }}
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
/* istanbul ignore file */
import {
  computed, ref, onMounted, watch, defineAsyncComponent,
  watchEffect,
} from 'vue';
import { useToggle } from '@vueuse/core';
import { useProduct } from '@modules/product';
import { useFeatureEligibility } from '@modules/featureControl';
import { useCommonProductGetters } from '@composables/useCommonProductGetters';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useProductData } from '@composables/useProductData';
import { useSlideout } from '@composables/utils/useSlideout';
import { useExperiments } from '@composables/useExperiments';
import SalesFloorWidget from '@components/SalesFloorWidget';
import { useBrand } from '@composables/useBrand';
import {
  useProductDetailsProvide,
  useProductDetails,
  useProductDetailsAvailabilityProvide,
  useProductDetailsAvailability,
} from '../../composables';
import tagViewMoreViewLessToggle from '../../../../features/analytics/productDetails';

import Summary from '../Summary';
import Dimensions from '../Dimensions';
import MaterialsAndCare from '../MaterialsAndCare';
import Instructions from '../Instructions';
import TermsAndConditions from '../TermsAndConditions';
import SizeAndFit from '../SizeAndFit';
import WhiteGloveDelivery from '../WhiteGloveDelivery';
import ShippingAndReturns from '../ShippingAndReturns';
import Ingredients from '../Ingredients';

/* istanbul ignore file */

useProductDetailsProvide();
useProductDetailsAvailabilityProvide();

const { productId } = useProduct();
const product = useProductData(productId);
const {
  isMobile, isDesktop, isTablet, isDesktopMedium,
} = useDeviceType();
const { bigTicketDeliveryMethods } = useCommonProductGetters();
const { productDescription, dimensionsTitle } = useProductDetails();
const { isShoppable, shoppableRedesignOptimization } = useFeatureEligibility();
const {
  isSummaryAvailable,
  isSizeAndFitAvailable,
  isIngredientsAvailable,
  isDimensionsAvailable,
  isMaterialAndCareAvailable,
  isInstructionsAvailable,
  isTermsAndConditionsAvailable,
  isShippingAndReturnsAvailable,
  isWhiteGloveDeliveryAvailable,
  isSalesFloorWidgetAvailable,
} = useProductDetailsAvailability();

const { showSlideout } = useSlideout();
// ProductDetailsDrawer exp flag represents 1st arm, but this view mounts for second arm only
// TODO: check the requirements and remove unnecessary code
const { experimentFlags: { productDetailsDrawer } } = useExperiments();
const { isBcom } = useBrand();

const CLOSE_WRAPPER_HEIGHT = isShoppable ? 120 : 240;
const spacingClass = shoppableRedesignOptimization ? 'margin-top-s' : 'medium-margin-top-s small-margin-top-xs';
const wrapper = ref(null);
const isSwitchButtonAvailable = ref(false);
const columnsArr = ref([
  {
    isAvailable: isSummaryAvailable.value,
    component: Summary,
    title: isBcom ? 'Features' : 'Product Features',
    dataAuto: 'product-summary-section',
  },
  {
    isAvailable: isSizeAndFitAvailable,
    component: SizeAndFit,
    title: 'Size & Fit',
    dataAuto: 'size-fit-section',
  },
  {
    isAvailable: isIngredientsAvailable,
    component: Ingredients,
    title: dimensionsTitle,
    dataAuto: 'product-ingredients-section',
  },
  {
    isAvailable: isDimensionsAvailable,
    component: Dimensions,
    title: 'Product Dimensions',
    dataAuto: 'product-dimensions-section',
  },
  {
    isAvailable: isMaterialAndCareAvailable,
    component: MaterialsAndCare,
    title: 'Materials & Care',
    dataAuto: 'materials-care-section',
  },
  {
    isAvailable: isInstructionsAvailable,
    component: Instructions,
    title: 'Instructions',
    dataAuto: 'instructions-section',
  },
  {
    isAvailable: isTermsAndConditionsAvailable,
    component: TermsAndConditions,
    title: 'Terms & Conditions',
    dataAuto: 'terms-conditions-section',
  },
  {
    isAvailable: isShippingAndReturnsAvailable,
    component: ShippingAndReturns,
    title: 'Shipping & Returns',
    dataAuto: 'shipping-returns-section',
  },
  {
    isAvailable: isWhiteGloveDeliveryAvailable,
    component: WhiteGloveDelivery,
    title: bigTicketDeliveryMethods.value?.length ? 'Delivery Methods' : 'White Glove Delivery',
    dataAuto: 'white-glove-delivery-section',
  },
  {
    isAvailable: isSalesFloorWidgetAvailable,
    isSalesfloorWidget: true,
    component: SalesFloorWidget,
    title: '',
    dataAuto: 'sales-floor-widget-section',
  },
]);
const availableColumns = computed(() => columnsArr.value.filter((c) => c.isAvailable));
const availableColumnsCount = computed(() => availableColumns.value.length);
const columns = ref(availableColumns.value);

/**
 * Splits an array into a specified number of subarrays by distributing the elements cyclically.
 * If an item has `isSalesfloorWidget = true`, it is always pushed to the last subarray
 * and placed as the last element in that subarray.
 *
 * For example:
 * Input: [0, 1, { isSalesfloorWidget: true }, 3, 4, 5, 6], count = 3
 * Output: [[0, 3, 6], [1, 4], [5, { isSalesfloorWidget: true }]]
 *
 * @param {Array[any]} array - The input array to split.
 * @param {number} count - The number of subarrays to create.
 * @returns {Array[any]} - An array containing the subarrays with distributed elements.
 */
function splitArray(array, count) {
  const result = Array.from({ length: count }, () => []);

  let salesfloorWidget = null;

  array.forEach((item, index) => {
    if (item.isSalesfloorWidget) {
      salesfloorWidget = item;
    } else {
      result[index % count].push(item);
    }
  });

  if (salesfloorWidget) {
    result[count - 1].push(salesfloorWidget);
  }

  return result;
}

/**
 * Determines the number of columns to display for the desktop layout.
 *
 * The function calculates the number of columns based on the available columns
 * and the presence of a product description. It uses a default value of 3 columns
 * but adjusts it under specific conditions:
 * - If the available columns count is less than 3, it defaults to 2 columns.
 * - If the available columns count is greater than 3 and the product description
 *   is not provided, it defaults to 4 columns.
 *
 * @returns {number} The number of columns to display for the desktop layout.
 */
function getColumnCountForDesktop() {
  const DEFAULT_COLUMN_COUNT = 3;
  let result = DEFAULT_COLUMN_COUNT;

  if (availableColumnsCount.value < DEFAULT_COLUMN_COUNT) {
    result = 2;
  } else if (availableColumnsCount.value > DEFAULT_COLUMN_COUNT && !productDescription.value) {
    result = 4;
  }
  return result;
}

watchEffect(() => {
  if (isMobile.value) {
    columns.value = splitArray(availableColumns.value, 1);
  } else if (isTablet.value) {
    columns.value = splitArray(availableColumns.value, 2);
  } else if (isDesktopMedium.value || isDesktop.value) {
    const count = getColumnCountForDesktop();
    columns.value = splitArray(availableColumns.value, count);
  }
});

const [isOpen, toggle] = useToggle(false);
const buttonText = computed(() => (isOpen.value ? 'View less' : 'View more'));

const additionalClasses = computed(() => ({
  'long-description-available': !!productDescription.value,
  'three-columns': availableColumnsCount.value === 3,
  'more-than-three': availableColumnsCount.value > 3,
  'less-than-three': availableColumnsCount.value < 3,
  'less-than-two': availableColumnsCount.value < 2,
}));

function getSwitchButtonAvailability() {
  if (isMobile.value) return true;

  const wrapperClientHeight = wrapper.value.clientHeight || 0;
  return wrapperClientHeight > CLOSE_WRAPPER_HEIGHT;
}
// This never runs - since productDetailsDrawer exp flag represents 1st arm, but this view mounts for second arm only
// TODO: check the requirements and remove unnecessary code
const showProductDetailsDrawer = () => {
  showSlideout({
    id: 'product-details-drawer',
    title: 'Product Details',
    component: defineAsyncComponent(/* istanbul ignore next */ () => import(/* webpackChunkName: "ProductDetailsDrawer" */ '../slideouts/ProductDetailsDrawer')),
    backable: false,
    props: {
      productId,
      product,
    },
  });
};

const handleButtonClick = () => {
  // This never runs - since productDetailsDrawer exp flag represents 1st arm, but this view mounts for second arm only
  // TODO: check the requirements and remove unnecessary code
  if (productDetailsDrawer) {
    showProductDetailsDrawer();
    tagViewMoreViewLessToggle(true);
  } else {
    toggle(!isOpen.value);
  }
};

watch(isOpen, () => {
  tagViewMoreViewLessToggle(isOpen.value);
});

onMounted(() => {
  isSwitchButtonAvailable.value = getSwitchButtonAvailability();
});
</script>

<style lang="scss">
@import "~settingSCSS";

#product-details {
  ul.column {
    list-style: none;

    ul {
      list-style: disc;
    }
  }

  .column-item:not(:last-child) {
    @include margin-bottom-s;
  }
}
</style>

<style lang="scss" scoped>
@import "~settingSCSS";

#product-details {
  :deep(.salesfloor) {
    // Rewrite salesfloor styles
    margin-top: 0px!important;

    .sf-virtual-advisor--expanded {
      margin-top: 0px!important;
    }

    .sf-virtual-advisor--stylist-title {
      @include body(medium);
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .product-id {
    color: $gray-1-color;
  }

  .wrapper {
    display: grid;
    grid-template-areas:
      'longDescription'
      'details';
    grid-template-columns: 1fr;
    background-color: $background-color;
    @include padding-left-xxs;
    @include padding-right-xxs;
    @include padding-top-xs;
    @include padding-bottom-xs;
  }

  .details {
    list-style: none;
    margin: 0;
    padding: 0;
    grid-area: details;
    display: grid;
    grid-template-columns: 1fr;
    gap: rem-calc(16px);
  }

  .long-description {
    grid-area: longDescription;
  }
}

@include breakpoint(small up) {
  #product-details {
    .expandable-wrapper {
      height: var(--blur-height);
      overflow: hidden;
      position: relative;

      &.open {
        height: auto;

        .switch-wrapper {
          @include padding-bottom-xs;
          position: relative;
          height: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          background: $background-color;
          cursor: default;
        }
      }

      &:not(.open) {
        .switch-wrapper {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 96px;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.50) 28.13%, #FFF 72.4%);
        }

        .switch {
          position: absolute;
          bottom: 0;
        }
      }
    }
  }
}

@include breakpoint(small only) {
  #product-details {
    .product-id {
      @include body(small);
      color: $gray-1-color;
    }

    .long-description {
      @include margin-bottom-xs;
    }

    .expandable-wrapper {
      &.open {
        .switch-wrapper {
          @include padding-left-xxs;
          justify-content: flex-start;
        }
      }

      &:not(.open) {
        .switch {
          position: absolute;
          bottom: 0;
          left: 8px;
        }
      }
    }
  }
}

@include breakpoint(medium up) {
  #product-details {
    .switch {
      cursor: pointer;
    }

    .wrapper {
      grid-template-areas:
        'longDescription longDescription'
        'details details';
      column-gap: 0;
      @include padding-left-xs;
      @include padding-right-xs;
      @include padding-top-s;
      @include padding-bottom-s;

      &.long-description-available {
        row-gap: rem-calc(24px);
        column-gap: rem-calc(24px);
        grid-template-columns: 1fr 2fr;
        &.more-than-three,
        &.three-columns {
          grid-template-columns: 1fr 3fr;
        }
        &.less-than-two {
          grid-template-columns: 1fr 1fr;

          .details {
            grid-template-columns: 1fr;
          }
        }
      }
    }

    .details {
      gap: rem-calc(24px);
      grid-template-columns: repeat(2, 1fr);
    }

  }
}

@include breakpoint(large up) {
  #product-details {
    .wrapper {
      grid-template-areas:
        'longDescription details';
      column-gap: 0px;

      .details {
        grid-template-columns: repeat(3, 1fr);
      }

      &:not(.long-description-available) {
        grid-template-areas:
          'details details';

        &.less-than-two {
          row-gap: 0;

          .details {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        &.less-than-three {
          .details {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        &.more-than-three {
          .details {
            grid-template-columns: repeat(4, 1fr);
          }
        }
      }

      &.long-description-available {
        row-gap: 0;
        &.less-than-two,
        &.less-than-three {
          grid-template-columns: 1fr 2fr;

          .details {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped brand="mcom">
@import "~settingSCSS";

@include breakpoint(small only) {
  #product-details {
    .title {
      @include header(h5);
    }
  }
}
</style>

<style lang="scss" scoped brand="bcom">
@import "~settingSCSS";

#product-details {

  h4 {
    @include body(small);
    text-transform: uppercase;
    font-weight: 700;
  }

  :deep(.salesfloor) {
    // Rewrite salesfloor styles
    .sf-virtual-advisor--stylist-title {
      @include body(small);
      text-transform: uppercase;
      font-weight: 700;
    }
  }
}

@include breakpoint(small only) {
  #product-details {
    .title {
      @include header(h5);
    }
  }
  #product-details.details-optimization {
    .title {
      @include header(h3);
    }
  }
}
</style>

<style lang="scss" brand="bcom">
@import "~settingSCSS";

#product-details {
  b {
    @include body(small);
    text-transform: uppercase;
    font-weight: 700;
  }
}
</style>
